import { ValidationSchema, Tuple } from '../../../types';
import coreActivityNameValidator from './name';
import { CoreActivityItemModel, YesNoEnumDesc } from '../../../api/models';
import { NotEmpty } from '../../../utils';
import * as validatorRules from '../../validatorRules.json';
import { isEmpty, isNaN } from 'lodash';
import { alphanumericBasic, fieldRequiredValidatorConditional, isABNUnique, numberMandatory } from '../../../validation/validationFunctions';
// tslint:disable-next-line:max-line-length
import { textMandatory, maxLength, matchAlphanumericExtended, textAreaMax, textAreaBetweenMinAndMax, minLengthWithTrim, maxLengthWithTrim, fieldRequiredValidator, dateRequireValidator } from '../../validationFunctions';
import { mandatoryString } from '../../../validation/util';
import { coreActivityDurationSoftValidator, coreActivityDurationHardValidator } from './duration';
import { maxCurrencyLength, mandatoryCurrency, greaterThanOrEqualZero } from '../../validationFunctions/commonCurrencyInput';
import { interpolateValidationRule } from '../../validatorMessages';
// import { Radiobutton } from 'components/input/radiobutton';
import * as DisplayRule from '../../../displayFunctions/RnDActivitiesForm/coreActivities';
import { subsidiaryMemberDetailsItemsNotEmpty } from '../applicationInclusions';

const companyNameMaxLength = 250;

const mandatoryArray: Tuple<(value: any) => boolean, string>[] = [
    [textMandatory, validatorRules.BR1_Mandatory_Field.FieldMessage],
];

const mandatoryArrayWithCondition = (condition?: () => boolean): [(value: any | undefined) => boolean, string][] => {
    if (!condition || condition()) {
        return mandatoryArray;
    }
    return [];
};

export const radioButtonMandatory: Tuple<(value: any) => boolean, string>[] = [
    [textMandatory, validatorRules.BR1_Mandatory_Field.FieldMessage],
];

export const evidenceAvailableOtherValidators: Tuple<(value: any) => boolean, string>[] = [
    [maxLength(100), interpolateValidationRule(validatorRules.BR3_Maximum_Field_Length, [], ['100'])],
    [alphanumericBasic, validatorRules.BR11_Alphanumeric_Basic_Format.FieldMessage],
];

const companyNameValidator = (values: Partial<CoreActivityItemModel>): Tuple<(value: any) => boolean, string>[] => {
    if (values.activitiesPerformedBy !== undefined && values.activitiesPerformedBy !== 'OnlyTheRnDCompany') {
        return [
            [maxLength(companyNameMaxLength), interpolateValidationRule(validatorRules.BR3_Maximum_Field_Length, [], [companyNameMaxLength.toString()])],
            [alphanumericBasic, validatorRules.BR11_Alphanumeric_Basic_Format.FieldMessage],
        ];
    }
    return [];
};

const validateSubsidiaryConductAbn = (
    values: Partial<CoreActivityItemModel>,
    condition?: () => boolean): Tuple<(value: any) => boolean, string>[] => {
    if (!condition || condition()) {
        return [
            [value => isABNUnique(value, values.afiRnDEntityABN), validatorRules.BR169_Subsidiary_RnDEntity_Unique.FieldMessage],
            [value => isABNUnique(value, values.primaryTaxAgentAbn), validatorRules.BR171_Subsidiary_TaxAgent_Unique.FieldMessage],
            [value => isABNUnique(value, values.organisationUhcAbn), validatorRules.BR173_Subsidiary_UHCDetails_Unique.FieldMessage],
        ];
    }
    return [];
};

export const coreActivities: ValidationSchema<Partial<CoreActivityItemModel>> = (values: Partial<CoreActivityItemModel>) => {
    return {
        name: coreActivityNameValidator(values),                                                                        // Q.62
        describeCoreActivity: textAreaBetweenMinAndMax('none', 'describeCoreActivity', 2, 4000),                        // Q.63
        isActivityExcludedFromCore: [
            [value => value !== YesNoEnumDesc.Yes, 'You are unable to register this activity as a core R&D activity.'],  // Q.65
        ],
        companyName: companyNameValidator(values),                                                                      // Q.67
        // tslint:disable-next-line:max-line-length                                                                     // Q.71
        subsidiaryConductAbn: validateSubsidiaryConductAbn(values, () => DisplayRule.isHeadMecGroup(values) && DisplayRule.activitiesPerformedBy123(values) && values.activityConductedBy === 'Subsidiary'),
        duration: coreActivityDurationHardValidator(values),                                                            // Q.74
        determinationRelyingOn: textAreaBetweenMinAndMax('none', 'determinationRelyingOn', 2, 4000),                    // Q.77
        sourcesInvestigated: textAreaBetweenMinAndMax('none', 'sourcesInvestigated', 2, 4000),                          // Q.79
        competentProfesionalReason: textAreaBetweenMinAndMax('none', 'competentProfesionalReason', 600, 4000),          // Q.80
        whyNoInvestigation: textAreaBetweenMinAndMax('none', 'whyNoInvestigation', 2, 1000),                            // Q.81
        hypothesis: textAreaBetweenMinAndMax('none', 'hypothesis', 650, 4000),                                          // Q.82
        experiment: textAreaBetweenMinAndMax('none', 'experiment', 650, 4000),                                          // Q.83
        evaluation: textAreaBetweenMinAndMax('none', 'evaluation', 530, 4000),                                          // Q.84
        conclusions: textAreaBetweenMinAndMax('none', 'conclusions', 340, 4000),                                        // Q.85
        evidenceAvailableOther: evidenceAvailableOtherValidators,                                                       // Q.87
        newKnowledge: textAreaBetweenMinAndMax('none', 'newKnowledge', 600, 4000),                                      // Q.89
        briefDescription: textAreaBetweenMinAndMax('none', 'briefDescription', 50, 2000),                               // Q.90.1 or 91.1
        expenditure: [maxCurrencyLength(12)],                                                                           // Q.92
        expenditurePriorToYear1: [maxCurrencyLength(12)],                                                               // Q.93.1
        financialYear1EstimatedExpenditure: [maxCurrencyLength(12)],                                                    // Q.93.2
        financialYear2EstimatedExpenditure: [maxCurrencyLength(12)],                                                    // Q.93.3
        financialYear3EstimatedExpenditure: [maxCurrencyLength(12)],                                                    // Q.93.4
        financialYear3EstimatedExpenditurePost: [maxCurrencyLength(12)],                                                // Q.93.5
    };
};

export const coreActivitiesSoft: ValidationSchema<Partial<CoreActivityItemModel>> = (values: Partial<CoreActivityItemModel>) => {
    return {
        // Q.61
        isToListPreviousCoreActivity: mandatoryString(),

        // Q.63
        describeCoreActivity: mandatoryString(),

        // Q.64
        projectId: [
            [value => NotEmpty(value), validatorRules.BR1_Mandatory_Field.FieldMessage],
            [value => !isNaN(Number(value)), validatorRules.BR1_Mandatory_Field.FieldMessage], // this should never fire in real use of the app
        ],

        // Q.65
        isActivityExcludedFromCore: [
            [textMandatory, validatorRules.BR1_Mandatory_Field.FieldMessage],
        ],

        // Q.66
        activitiesPerformedBy: mandatoryString(),

        // Q.67
        companyName: mandatoryString(() => DisplayRule.activitiesPerformedBy234(values)),

        // Q.68
        isEntityConductingActivityConnectedOrAffiliated: mandatoryString(() => DisplayRule.activitiesPerformedBy234(values)),

        /// Q.69
        isActivityRnDEntityLocatedOutsideAustralia: mandatoryString(() => DisplayRule.activitiesPerformedBy234(values)),

        // Q.70
        activityConductedBy: mandatoryString(() => DisplayRule.isHeadMecGroup(values) && DisplayRule.activitiesPerformedBy123(values)),

        // Q.71
        // tslint:disable-next-line:max-line-length
        subsidiaryConductAbn: fieldRequiredValidatorConditional(() => DisplayRule.isHeadMecGroup(values) && DisplayRule.activitiesPerformedBy123(values) && values.activityConductedBy === 'Subsidiary'),

        // Q.72
        companyCountryOfResidence: mandatoryString(() => DisplayRule.activitiesPerformedBy234(values)),

        // Q.73
        futureCoreActivity: mandatoryString(),

        // Q.74
        duration: coreActivityDurationSoftValidator(values),

        // Q.75
        // tslint:disable-next-line:max-line-length
        otherOrganisation: mandatoryString(() => DisplayRule.applicationInclusion234(values)),

        // Q.76
        isActivityCoveredByIRDD: mandatoryString(() => DisplayRule.otherOrganisationOptions134(values)),

        // Q.77
        determinationRelyingOn: mandatoryString(() => DisplayRule.otherOrganisationOptions134(values) && values.isActivityCoveredByIRDD === 'Yes'),

        // Q.78
        outcome: fieldRequiredValidatorConditional(() => DisplayRule.otherOrganisationOptions134(values)),

        // Q.79
        // tslint:disable-next-line:max-line-length
        sourcesInvestigated: mandatoryString(() => DisplayRule.outcomeOptions1234(values) && DisplayRule.otherOrganisationOptions134(values) && values.futureCoreActivity === 'No'),

        // Q.80
        // tslint:disable-next-line:max-line-length
        competentProfesionalReason: mandatoryString(() => DisplayRule.outcomeOptions1234(values) && DisplayRule.otherOrganisationOptions134(values) && values.futureCoreActivity === 'No'),

        // Q.81
        // tslint:disable-next-line:max-line-length
        whyNoInvestigation: mandatoryString(() => DisplayRule.otherOrganisationOptions134(values) && values.futureCoreActivity === 'No' && values.outcome !== undefined && values.outcome.includes('None')),

        // Q.82
        hypothesis: mandatoryString(() => DisplayRule.otherOrganisationOptions134(values)),

        // Q.83
        experiment: mandatoryString(() => DisplayRule.otherOrganisationOptions134(values)),

        // Q.84
        evaluation: mandatoryString(() => DisplayRule.otherOrganisationOptions134(values)),

        // Q.85
        conclusions: mandatoryString(() => DisplayRule.otherOrganisationOptions134(values) && values.futureCoreActivity === 'No'),

        // Q.86
        // tslint:disable-next-line:max-line-length
        evidenceAvailable: mandatoryArrayWithCondition(() => DisplayRule.otherOrganisationOptions134(values) && values.futureCoreActivity === 'No'),

        // Q.87
        // tslint:disable-next-line:max-line-length
        evidenceAvailableOther: mandatoryString(() => DisplayRule.otherOrganisationOptions134(values) && values.evidenceAvailable !== undefined && values.evidenceAvailable.includes('Other') && values.futureCoreActivity === 'No'),

        // Q.88
        purpose: mandatoryString(() => DisplayRule.otherOrganisationOptions134(values)),

        // Q.89
        newKnowledge: mandatoryString(() => DisplayRule.otherOrganisationOptions134(values) && values.purpose === 'Yes'),

        // Q.90, Q.91
        otherOrganisationName: mandatoryString(() => DisplayRule.otherOrganisationOptions123(values)),

        // Q.90.1, Q.91.1
        briefDescription: mandatoryString(() => DisplayRule.otherOrganisationOptions123(values)),

        // Q.92
        expenditure: [
            greaterThanOrEqualZero(),
            [value => numberMandatory(value) && DisplayRule.otherOrganisationOptions134(values), validatorRules.BR34_02_Currency.FieldMessage],
        ],

        // Q.93.1
        expenditurePriorToYear1: [
            greaterThanOrEqualZero(),
            [value => numberMandatory(value), validatorRules.BR34_02_Currency.FieldMessage],
        ],

        // Q.93.2
        financialYear1EstimatedExpenditure: [
            greaterThanOrEqualZero(),
            [value => numberMandatory(value), validatorRules.BR34_02_Currency.FieldMessage],
        ],

        // Q.93.3
        financialYear2EstimatedExpenditure: [
            greaterThanOrEqualZero(),
            [value => numberMandatory(value), validatorRules.BR34_02_Currency.FieldMessage],
        ],

        // Q.93.4
        financialYear3EstimatedExpenditure: [
            greaterThanOrEqualZero(),
            [value => numberMandatory(value), validatorRules.BR34_02_Currency.FieldMessage],
        ],

        // Q.93.5
        financialYear3EstimatedExpenditurePost: [
            greaterThanOrEqualZero(),
            [value => numberMandatory(value), validatorRules.BR34_02_Currency.FieldMessage],
        ],
    };
};
